// components/features/shops/ShopForm.tsx
import { useState, useEffect } from 'react';
import { Shop, Sector } from '@cabukaka/types';
import { MapPin, Phone, Store, Clock, Tag, Image as ImageIcon, Trash2 } from 'lucide-react';

interface ShopFormProps {
  initialData?: Partial<Shop>;
  sectors: Sector[];
  onSubmit: (data: any) => Promise<void>;
  isSubmitting?: boolean;
}

interface FormErrors {
  name?: string;
  description?: string;
  phone?: string;
  sectorId?: string;
  city?: string;
  logo?: string;
}

export default function ShopForm({
  initialData,
  sectors,
  onSubmit,
  isSubmitting = false
}: ShopFormProps) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    phone: initialData?.phone || '',
    sectorId: initialData?.sectorId || '',
    open: initialData?.open ?? true,
    deliveryTime: initialData?.deliveryTime || '24h',
    badge: initialData?.badge || '',
    location: {
      address: initialData?.location?.address || '',
      city: initialData?.location?.city || '',
      country: initialData?.location?.country || 'Cameroun',
      coords: {
        long: initialData?.location?.coords?.long || 0,
        lat: initialData?.location?.coords?.lat || 0
      }
    }
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [existingLogo, setExistingLogo] = useState<string>(initialData?.logo || '');
  const [existingCover, setExistingCover] = useState<string>(initialData?.coverImage || '');
  const [logoPreview, setLogoPreview] = useState<string>(initialData?.logo || '');
  const [coverPreview, setCoverPreview] = useState<string>(initialData?.coverImage || '');
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [logoFile]);

  useEffect(() => {
    if (coverFile) {
      const url = URL.createObjectURL(coverFile);
      setCoverPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [coverFile]);

const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
        const parts = name.split('.');
        const parent = parts[0] as keyof typeof formData;
        const child = parts[1];
        
        setFormData(prev => ({
            ...prev,
            [parent]: {
                ...(prev[parent] as any),
                [child!]: value
            }
        }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name as keyof FormErrors]) {
        setErrors(prev => ({ ...prev, [name]: undefined }));
    }
};
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setExistingLogo('');
    }
  };

  const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverFile(file);
      setExistingCover('');
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setExistingLogo('');
    setLogoPreview('');
  };

  const removeCover = () => {
    setCoverFile(null);
    setExistingCover('');
    setCoverPreview('');
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name) newErrors.name = 'Nom de la boutique requis';
    if (!formData.description) newErrors.description = 'Description requise';
    if (!formData.phone) newErrors.phone = 'Téléphone requis';
    if (!formData.location.city) newErrors.city = 'Ville requise';
    if (!logoFile && !existingLogo) newErrors.logo = 'Logo requis';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData = {
      ...formData,
      logoFile: logoFile,
      coverFile: coverFile,
      existingLogo: existingLogo,
      existingCover: existingCover
    };

    await onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Logo et Cover */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Logo *</label>
          <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50">
            {logoPreview ? (
              <>
                <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={removeLogo}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </>
            ) : (
              <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
                <ImageIcon className="w-8 h-8 text-gray-400" />
                <span className="text-xs text-gray-500 mt-1">Ajouter</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              </label>
            )}
          </div>
          {errors.logo && <p className="text-red-500 text-xs mt-1">{errors.logo}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Image de couverture</label>
          <div className="relative w-full h-32 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50">
            {coverPreview ? (
              <>
                <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={removeCover}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </>
            ) : (
              <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
                <ImageIcon className="w-8 h-8 text-gray-400" />
                <span className="text-xs text-gray-500 mt-1">Ajouter</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Informations générales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la boutique *</label>
          <div className="relative">
            <Store className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="Nom de votre boutique"
            />
          </div>
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
          <div className="relative">
            <Phone className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.phone ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="6 99 99 99 99"
            />
          </div>
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
        </div>
      </div>

      {/* Secteur et badge */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Secteur d'activité</label>
          <select
            name="sectorId"
            value={formData.sectorId}
            onChange={handleInputChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.sectorId ? 'border-red-300' : 'border-gray-300'}`}
          >
            <option value="">Sélectionner un secteur</option>
            {sectors.map(sector => (
              <option key={sector.id} value={sector.id}>{sector.name}</option>
            ))}
          </select>
          {errors.sectorId && <p className="text-red-500 text-xs mt-1">{errors.sectorId}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Badge</label>
          <div className="relative">
            <Tag className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              name="badge"
              value={formData.badge}
              onChange={handleInputChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              placeholder="Ex: Livraison gratuite"
            />
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
        <textarea
          name="description"
          rows={4}
          value={formData.description}
          onChange={handleInputChange}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
          placeholder="Description de votre boutique..."
        />
        {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
      </div>

      {/* Localisation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ville *</label>
          <div className="relative">
            <MapPin className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              name="location.city"
              value={formData.location.city}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${errors.city ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="Douala, Yaoundé..."
            />
          </div>
          {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
          <input
            type="text"
            name="location.address"
            value={formData.location.address}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
            placeholder="Adresse exacte"
          />
        </div>
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Délai de livraison</label>
          <div className="relative">
            <Clock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              name="deliveryTime"
              value={formData.deliveryTime}
              onChange={handleInputChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              placeholder="Ex: 24h, Livraison express"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 mt-7">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="open"
              checked={formData.open}
              onChange={handleCheckboxChange}
              className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
            />
            <span className="text-sm text-gray-700">Boutique ouverte</span>
          </label>
        </div>
      </div>

      {/* Boutons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition disabled:opacity-50"
        >
          {isSubmitting ? 'Enregistrement...' : (initialData?.id ? 'Modifier' : 'Créer')}
        </button>
      </div>
    </form>
  );
}